import { useEffect } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { CardHeader, Input, Select, CurrencyInput, DatePicker } from '@orientamos/ui'
import type { IntakeFormData } from '@orientamos/shared'

interface Step6Props {
  form: UseFormReturn<IntakeFormData>
}

const durationOptions = [
  { value: '6', label: '6 meses' },
  { value: '12', label: '12 meses' },
  { value: '24', label: '24 meses' },
  { value: '36', label: '36 meses' },
]

const rentIncreaseOptions = [
  { value: 'IPC', label: 'IPC (Indice de Precios al Consumidor)' },
  { value: 'IPC+1', label: 'IPC + 1%' },
  { value: 'IPC+2', label: 'IPC + 2%' },
  { value: 'fixed', label: 'Porcentaje fijo anual' },
]

const adminFeePaidByOptions = [
  { value: 'tenant', label: 'Arrendatario' },
  { value: 'owner', label: 'Propietario' },
]

export function Step6Terms({ form }: Step6Props) {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = form

  const startDate = watch('terms.startDate')
  const durationMonths = watch('terms.durationMonths')
  const adminFeePaidBy = watch('terms.adminFeePaidBy')
  const rentIncreaseFormula = watch('terms.rentIncreaseFormula')

  // Auto-calculate end date based on start date and duration
  useEffect(() => {
    if (
      startDate &&
      typeof startDate === 'string' &&
      typeof durationMonths === 'number' &&
      durationMonths > 0
    ) {
      const start = new Date(startDate)
      start.setMonth(start.getMonth() + durationMonths)
      // End date is the day before the anniversary
      start.setDate(start.getDate() - 1)
      const endDateStr = start.toISOString().split('T')[0] ?? ''
      setValue('terms.endDate', endDateStr)
    }
  }, [startDate, durationMonths, setValue])

  return (
    <div className="space-y-8">
      <CardHeader
        title="Condiciones del Arrendamiento"
        description="Define los terminos economicos y la duracion del contrato"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CurrencyInput
          label="Canon Mensual"
          placeholder="Ej: 2.500.000"
          {...register('terms.monthlyRent', { valueAsNumber: true })}
          error={errors.terms?.monthlyRent?.message}
        />

        <CurrencyInput
          label="Cuota de Administracion"
          placeholder="Ej: 350.000"
          {...register('terms.administrationFee', { valueAsNumber: true })}
          error={errors.terms?.administrationFee?.message}
        />

        <Select
          label="Administracion pagada por"
          options={adminFeePaidByOptions}
          value={adminFeePaidBy}
          onChange={(e) => setValue('terms.adminFeePaidBy', e.target.value as 'tenant' | 'owner')}
          error={errors.terms?.adminFeePaidBy?.message}
        />

        <Input
          label="Dias de Gracia para Pago"
          type="number"
          min={0}
          max={30}
          placeholder="5"
          {...register('terms.gracePeriodDays', { valueAsNumber: true })}
          error={errors.terms?.gracePeriodDays?.message}
        />
      </div>

      <div className="pt-6 mt-6 border-t border-neutral-200">
        <h4 className="text-base font-medium text-neutral-900 mb-4">Duracion del Contrato</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DatePicker
            label="Fecha de Inicio"
            {...register('terms.startDate')}
            error={errors.terms?.startDate?.message}
          />

          <Select
            label="Duracion"
            options={durationOptions}
            value={String(durationMonths)}
            onChange={(e) => setValue('terms.durationMonths', parseInt(e.target.value, 10))}
            error={errors.terms?.durationMonths?.message}
          />

          <DatePicker
            label="Fecha de Finalizacion"
            {...register('terms.endDate')}
            error={errors.terms?.endDate?.message}
            disabled
            hint="Calculada automaticamente"
          />
        </div>
      </div>

      <div className="pt-6 mt-6 border-t border-neutral-200">
        <h4 className="text-base font-medium text-neutral-900 mb-4">Incremento Anual</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Formula de Incremento"
            options={rentIncreaseOptions}
            value={rentIncreaseFormula}
            onChange={(e) => setValue('terms.rentIncreaseFormula', e.target.value)}
            error={errors.terms?.rentIncreaseFormula?.message}
          />

          {rentIncreaseFormula === 'fixed' && (
            <Input
              label="Porcentaje de Incremento"
              type="number"
              min={0}
              max={20}
              placeholder="Ej: 5"
              hint="Porcentaje anual fijo"
            />
          )}
        </div>
      </div>
    </div>
  )
}
